/** Inline icons — no icon-font dependency, and they inherit currentColor. */

const base = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  viewBox: '0 0 24 24',
};

/** Lens aperture — the studio mark. */
export const ModelsIcon = () => (
  <svg {...base} aria-hidden="true">
    <circle cx="12" cy="8" r="3.4" />
    <path d="M4.8 20a7.2 7.2 0 0 1 14.4 0" />
  </svg>
);

export const ShootIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M3 8.5h3l1.5-2.5h9L18 8.5h3v10H3z" />
    <circle cx="12" cy="13" r="3.4" />
  </svg>
);

/** A roll of film — one shoot. */
export const RollIcon = () => (
  <svg {...base} aria-hidden="true">
    <rect x="3.5" y="6" width="17" height="12" rx="1.6" />
    <path d="M3.5 9h17M3.5 15h17" />
    <path d="M7 6V4.5h5V6" />
  </svg>
);

export const GalleryIcon = () => (
  <svg {...base} aria-hidden="true">
    <rect x="3" y="4.5" width="18" height="15" rx="1.6" />
    <path d="M3 15.5l4.5-3.5 3.5 2.6 3-2.6 6.5 5" />
    <circle cx="8.3" cy="9" r="1.2" />
  </svg>
);

export const MicIcon = () => (
  <svg {...base} width="15" height="15" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" />
  </svg>
);

export const StopIcon = () => (
  <svg {...base} width="15" height="15" aria-hidden="true">
    <rect x="6.5" y="6.5" width="11" height="11" rx="1.5" fill="currentColor" />
  </svg>
);

export const PlusIcon = () => (
  <svg {...base} width="16" height="16" aria-hidden="true">
    <path d="M12 5.5v13M5.5 12h13" />
  </svg>
);

/** Shutter burst — "generate". */
export const SparkIcon = () => (
  <svg {...base} width="16" height="16" aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2.6v3.1M12 18.3v3.1M2.6 12h3.1M18.3 12h3.1M5.4 5.4l2.2 2.2M16.4 16.4l2.2 2.2M18.6 5.4l-2.2 2.2M7.6 16.4l-2.2 2.2" />
  </svg>
);

/** The way forward on a card or a call to action. */
export const ArrowRightIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/** A step answered. */
export const CheckIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

export const DownloadIcon = () => (
  <svg {...base} width="15" height="15" aria-hidden="true">
    <path d="M12 4v10m0 0l-3.5-3.5M12 14l3.5-3.5M5 18.5h14" />
  </svg>
);

export const TrashIcon = () => (
  <svg {...base} width="15" height="15" aria-hidden="true">
    <path d="M4.5 7h15M9.5 7V5h5v2M6.5 7l1 12.5h9L17.5 7" />
  </svg>
);

export const PencilIcon = () => (
  <svg {...base} width="15" height="15" aria-hidden="true">
    <path d="M4.5 19.5l1-4L16 5l3 3L8.5 18.5z" />
    <path d="M13.5 7.5l3 3" />
  </svg>
);

export const BackIcon = () => (
  <svg {...base} width="16" height="16" aria-hidden="true">
    <path d="M14.5 5.5L8 12l6.5 6.5" />
  </svg>
);

/*
 * An editor marks a contact sheet with a grease pencil: a ring around a keeper,
 * a cross through a reject. These are drawn loose, off-centre and slightly
 * over-closed, so they read as hand marks rather than UI checkboxes.
 */

export const KeepIcon = () => (
  <svg {...base} strokeWidth={2} width="17" height="17" aria-hidden="true">
    <path d="M15.8 6.2c-3.4-2.5-9.2-1.8-11 1.6-1.8 3.4.6 8 4.6 9 4 1 8.4-1.4 8.6-5 .2-2.6-1.8-4.7-4.4-5.4" />
  </svg>
);

export const RejectIcon = () => (
  <svg {...base} strokeWidth={2} width="17" height="17" aria-hidden="true">
    <path d="M5.6 5.2 18.6 18.9M18.4 5.4 5.4 18.7" />
  </svg>
);

/** A loupe over a contact sheet — inspect this frame. */
export const LoupeIcon = () => (
  <svg {...base} width="15" height="15" aria-hidden="true">
    <circle cx="10.5" cy="10.5" r="6" />
    <path d="M15 15l4.5 4.5" />
  </svg>
);

/** A key on a ring — the account and its locks. */
export const AccountIcon = () => (
  <svg {...base} aria-hidden="true">
    <circle cx="9" cy="12" r="4" />
    <path d="M13 12h7.5M18 12v3M16 12v2.2" />
  </svg>
);

/** Bars on a baseline — the operator's dashboard. */
export const ChartIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M4 19.5h16" />
    <path d="M7 19.5V12M12 19.5V6.5M17 19.5v-5" />
  </svg>
);

/** Three bars: opens and closes the rail. */
export const MenuIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

/** The same three bars, crossed out — the rail is open, this shuts it. */
export const CloseIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/** Three dots: the row has more to it than the row shows. */
/** A door with an arrow leaving it. */
export const SignOutIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M14.5 4.5H6.2A1.7 1.7 0 0 0 4.5 6.2v11.6a1.7 1.7 0 0 0 1.7 1.7h8.3" />
    <path d="M16 8.5L19.5 12 16 15.5M19.5 12H9.5" />
  </svg>
);

/** A pencil. */
export const RenameIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M15.5 4.8l3.7 3.7M4.5 19.5l.9-3.8L16.1 5.1a1.4 1.4 0 0 1 2 0l.8.8a1.4 1.4 0 0 1 0 2L8.3 18.6z" />
  </svg>
);

/** A magnifier. */
export const SearchIcon = () => (
  <svg {...base} aria-hidden="true">
    <circle cx="10.8" cy="10.8" r="6.3" />
    <path d="M15.4 15.4l4.1 4.1" />
  </svg>
);

/** A chevron, pointing the way a row opens. */
export const ChevronIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M9 5.5l6.5 6.5L9 18.5" />
  </svg>
);

/**
 * The studio's mark: a rosette of six petals around an open centre.
 *
 * Filled rather than stroked, because at 26px a 1.6px outline of six
 * overlapping shapes turns to grey mush. It is an aperture seen head on —
 * the blades of a lens, which is the one object every page here is about.
 */
export const MarkIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    {[0, 60, 120, 180, 240, 300].map((deg) => (
      <ellipse key={deg} cx="12" cy="7.1" rx="3.05" ry="4.55" transform={`rotate(${deg} 12 12)`} />
    ))}
    <circle cx="12" cy="12" r="2.15" fill="var(--panel)" />
  </svg>
);

/** A tick, for the box that says a frame is kept. */
export const TickIcon = () => (
  <svg {...base} strokeWidth={2.4} aria-hidden="true">
    <path d="M5 12.6l4.4 4.4L19 7.4" />
  </svg>
);

/** A dash: nobody has judged this one yet. */
export const DashIcon = () => (
  <svg {...base} strokeWidth={2.2} aria-hidden="true">
    <path d="M7.5 12h9" />
  </svg>
);

/** Sliders: the settings of a thing. */
export const SlidersIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M4 7.5h10M17.5 7.5H20M4 16.5h3M10.5 16.5H20" />
    <circle cx="15.6" cy="7.5" r="2.1" />
    <circle cx="8.6" cy="16.5" r="2.1" />
  </svg>
);

/** A shield: the part of an account that is about getting in. */
export const ShieldIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M12 3.6l7 2.6v5.2c0 4.2-2.9 7.6-7 9.2-4.1-1.6-7-5-7-9.2V6.2z" />
  </svg>
);

/** A card, for the part of an account that is about money. */
export const CardIcon = () => (
  <svg {...base} aria-hidden="true">
    <rect x="3.2" y="5.6" width="17.6" height="12.8" rx="2.6" />
    <path d="M3.2 10h17.6" />
  </svg>
);

/** Daylight. */
export const SunIcon = () => (
  <svg {...base} aria-hidden="true">
    <circle cx="12" cy="12" r="4.1" />
    <path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7" />
  </svg>
);

/** After dark. */
export const MoonIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M20 14.4A8.2 8.2 0 0 1 9.6 4a8.4 8.4 0 1 0 10.4 10.4z" />
  </svg>
);

/**
 * Whatever the machine is doing: a disc lit on one side.
 *
 * The half is filled rather than outlined, because at sixteen pixels a
 * half-circle drawn in strokes is indistinguishable from the sun beside it.
 */
export const AutoIcon = () => (
  <svg {...base} aria-hidden="true">
    <circle cx="12" cy="12" r="8" />
    <path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" stroke="none" />
  </svg>
);

/** A triangle with a bar in it: something here cannot be taken back. */
export const AlertIcon = () => (
  <svg {...base} aria-hidden="true">
    <path d="M12 4.2 21 19.4H3z" />
    <path d="M12 10v4M12 16.6v.1" />
  </svg>
);
